import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useFirestoreDoc from "../../../app/hooks/useFirestoreDoc";
import { listenToDocFromFirestore } from "../../../app/firestore/firestoreService";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { Redirect, useHistory } from "react-router-dom";
import { Grid } from "semantic-ui-react";
import { TEAL_BOX } from "../../../css/styles";
import {
  EMPLOYER_DASHBOARD_MANAGE_JOBS_PATH,
  EMPLOYER_PROMOTION_SERVICES_PATH,
} from "../../../app/api/routeConstants";
import sortBy from "lodash/sortBy";
import { differenceInCalendarDays, format } from "date-fns";
import ReactToPrint from "react-to-print";
import print from "../../../assets/icons/print.svg";
import {
  FIRESTORE_COMPANY_PROFILE_METRICS_COLLECTION,
  FIRESTORE_COMPANY_PROFILES_STATUS_LOG_COLLECTION,
  QUERY_CODES,
} from "../../../app/firestore/firestoreConstants";
import Statistic from "../../../app/common/components/Statistic";
import linkedinIcon from "../../../assets/icons/iconmonstr-linkedin-3.png";
import twitterIcon from "../../../assets/icons/twitter-icon.png";
import facebookIcon from "../../../assets/icons/facebook-icon.png";
import instaIcon from "../../../assets/icons/insta-icon.png";
import BackButton from "../../../app/common/components/BackButton";

export default function CompanyMetricsPane({
  company,
  handleBack,
  isSubscription = false,
}) {
  const dispatch = useDispatch();
  const history = useHistory();

  const printRef = useRef();

  const { currentUserAccount } = useSelector((state) => state.account);
  const { loading, error } = useSelector((state) => state.async);
  const { companyFeaturedLogos } = useSelector((state) => state.promotions);

  const [statusLog, setStatusLog] = useState(null);
  const [metrics, setMetrics] = useState(null);

  const employerLiveCLFs = companyFeaturedLogos.filter(
    (logo) =>
      logo.employerId === currentUserAccount.id &&
      logo.isInitialised &&
      logo.isApproved
  );

  useFirestoreDoc({
    shouldExecute: company,
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_COMPANY_PROFILES_STATUS_LOG_COLLECTION,
        id: company.refName,
      }),
    data: (log) => setStatusLog(log),
    deps: [company, dispatch],
    customErrorMessage: "Company Profile History not found",
    queryCode: QUERY_CODES.listenToServiceStatusLogFromFirestore,
    params: company,
  });

  useFirestoreDoc({
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_COMPANY_PROFILE_METRICS_COLLECTION,
        id: company.refName,
      }),
    data: (companyMetrics) => setMetrics(companyMetrics),
    deps: [company, dispatch],
    queryCode: QUERY_CODES.listenToCompanyMetrics,
    params: company,
  });

  // const paymentIntentId = jobPostOrder?.paymentIntentId;
  // companyProfileOrders have unique ID but refName same
  // useFirestoreCollection to fetch them and list them (pay and renews)

  if (error && !company) {
    return <Redirect to={EMPLOYER_DASHBOARD_MANAGE_JOBS_PATH} />;
  }

  if (loading || !currentUserAccount || !statusLog || !metrics)
    return <LoadingComponent content="Loading company activity" />;

  if (error) return <Redirect to="/error" />;

  const today = new Date();

  const daysActive = differenceInCalendarDays(today, company.datePublished);
  const daysUntilExpiry = differenceInCalendarDays(company.dateExpiring, today);

  function isPlural(number) {
    return number === 1 ? "" : "s";
  }

  return (
    <div ref={printRef} style={{ marginTop: 28 }} className="body-blue-text">
      <div className="hello-radius-logo-print">
        <img
          style={{ height: 100 }}
          src={
            "https://firebasestorage.googleapis.com/v0/b/helloradius-caf9d.appspot.com/o/photos%2Flogohelloradiusname.png?alt=media&token=c4740341-8645-4eed-ab0a-047f219d5a48"
          }
          alt="hello radius logo"
        />
      </div>
      <BackButton action={handleBack} />
      {company.name ? (
        <Grid container style={{ paddingTop: 30 }} stackable>
          <Grid.Row style={{ paddingLeft: 16 }}>
            <Grid.Column width={15}>
              <span style={{ fontSize: 24, fontWeight: 600 }}>
                Profile Activity
              </span>
              <span style={{ fontSize: 19, fontWeight: 500, margin: "0 4px" }}>
                {" • "}
              </span>
              <span style={{ fontSize: 24, fontWeight: 400 }}>
                {company.name}
              </span>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row style={{ paddingLeft: 16, paddingTop: 0 }}>
            <Grid.Column width={6}>
              <span style={{ fontSize: 14, fontWeight: 400 }}>
                REF: {company.refName}
              </span>
            </Grid.Column>
            <Grid.Column width={4} textAlign={"center"}>
              Active for:{" "}
              {daysActive === 0 && <span className="semi-bold">Today</span>}
              {daysActive !== 0 && (
                <span className="semi-bold">
                  {daysActive} day{isPlural(daysActive)}
                </span>
              )}
            </Grid.Column>
            {!isSubscription && (
              <Grid.Column width={4} textAlign={"center"}>
                Expires in:{" "}
                <span className="semi-bold">
                  {daysUntilExpiry} day{isPlural(daysUntilExpiry)}
                </span>
              </Grid.Column>
            )}
          </Grid.Row>
          <Grid.Row style={{ paddingLeft: 30, paddingTop: 0 }}>
            <ReactToPrint
              trigger={() => (
                <div className="print-button clickable">
                  <span>
                    <img className="print-icon" src={print} alt="menu" />
                  </span>
                </div>
              )}
              content={() => printRef.current}
            />
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5}>
              <Statistic
                headingTextClass={"h3-text"}
                gap={10}
                heading="Profile visits"
                stat={metrics.companyViews}
                style={{ minHeight: 122 }}
              />
            </Grid.Column>
            <Grid.Column width={5}>
              <Statistic
                headingTextClass={"h3-text"}
                gap={10}
                heading="Website Link clicks"
                stat={metrics.websiteLinkClicks}
                style={{ minHeight: 122 }}
              />
            </Grid.Column>
            <Grid.Column width={5}>
              <Statistic
                headingTextClass={"h3-text"}
                gap={10}
                heading="Featured Logo clicks"
                style={{ minHeight: 122 }}
                stat={
                  <div className="center-flex">
                    <span>{metrics.featuredLogoClicks}</span>
                    {!currentUserAccount.isAdmin &&
                      employerLiveCLFs.length === 0 && (
                        <button
                          className="button short-button orange-inverse-button"
                          style={{ marginLeft: 10 }}
                          onClick={() =>
                            history.push(EMPLOYER_PROMOTION_SERVICES_PATH)
                          }
                        >
                          Promote
                        </button>
                      )}
                  </div>
                }
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={9}>
              <div
                className="body-blue-text"
                style={{ ...TEAL_BOX, width: "100%" }}
              >
                <h3>Profile Status History</h3>
                {sortBy(statusLog.log, "date").map((log, index) => (
                  <div key={index}>
                    <span className="semi-bold">{log.status}: </span>
                    <span>
                      {format(
                        log.date.toDate(),
                        "dd MMMM yyyy - 'Time:' HH:mm"
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </Grid.Column>
            {([
              company.linkedInLink,
              company.twitterLink,
              company.facebookLink,
              company.instagramLink,
            ].filter((link) => link !== "").length > 0 ||
              [
                metrics.linkedInClicks,
                metrics.twitterClicks,
                metrics.facebookClicks,
                metrics.instagramClicks,
              ].filter((clicks) => clicks > 0).length > 0) && (
              <Grid.Column width={6}>
                <div
                  className="body-blue-text"
                  style={{ ...TEAL_BOX, width: "100%" }}
                >
                  <h3>Social Media Activity</h3>
                  {(company.linkedInLink || metrics.linkedInClicks > 0) && (
                    <div className="details-item display-flex">
                      <span style={{ width: 40, marginRight: 10 }}>
                        <img src={linkedinIcon} alt="social icon" />
                      </span>
                      <span style={{ paddingTop: 3 }}>
                        {metrics.linkedInClicks} visit
                        {isPlural(metrics.linkedInClicks)}
                      </span>
                    </div>
                  )}
                  {(company.twitterLink || metrics.twitterClicks > 0) && (
                    <div className="details-item display-flex">
                      <span style={{ width: 40, marginRight: 10 }}>
                        <img src={twitterIcon} alt="social icon" />
                      </span>
                      <span style={{ paddingTop: 3 }}>
                        <span style={{ paddingTop: 3 }}>
                          {metrics.twitterClicks} visit
                          {isPlural(metrics.twitterClicks)}
                        </span>
                      </span>
                    </div>
                  )}
                  {(company.facebookLink || metrics.facebookClicks > 0) && (
                    <div className="details-item display-flex">
                      <span style={{ width: 40, marginRight: 10 }}>
                        <img src={facebookIcon} alt="social icon" />
                      </span>
                      <span style={{ paddingTop: 3 }}>
                        <span style={{ paddingTop: 3 }}>
                          {metrics.facebookClicks} visit
                          {isPlural(metrics.facebookClicks)}
                        </span>
                      </span>
                    </div>
                  )}
                  {(company.instagramLink || metrics.instagramClicks > 0) && (
                    <div className="details-item display-flex">
                      <span style={{ width: 40, marginRight: 10 }}>
                        <img src={instaIcon} alt="social icon" />
                      </span>
                      <span style={{ paddingTop: 3 }}>
                        <span style={{ paddingTop: 3 }}>
                          {metrics.instagramClicks} visit
                          {isPlural(metrics.instagramClicks)}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      ) : (
        <Grid container textAlign="left">
          <Grid.Column width={3} />
          <Grid.Column width={10}>
            <div style={{ marginTop: 110 }}>
              <div style={{ fontSize: 38 }}>
                We're sorry, the company you are looking for can't be found.
              </div>
              <div className="body-blue-text" style={{ margin: "37px 0 24px" }}>
                It might have expired, been recently deleted or is temporarily
                unavailable and may be back soon.
              </div>
              <div className="body-blue-text">In the meantime...</div>
            </div>
            <Grid.Column width={3} />
          </Grid.Column>
        </Grid>
      )}
    </div>
  );
}
